import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FacebookShareButton from 'react-share/lib/FacebookShareButton';
import TwitterShareButton from 'react-share/lib/TwitterShareButton';
import EmailShareButton from 'react-share/lib/EmailShareButton';
import COLORS from '../../style/universal.color';
import { Label, Wrapper } from './share.style';
import { ButtonProps, LiProps, OpenDirection, ShareProps } from './share.types';
import Skeleton from '../Skeleton/skeleton';
import Icon from '../Icon/icon';
import { copyToClipboard } from '../../utils/browserUtils';

const itemVariants = (openDirection: OpenDirection) => {
  const opacity = 0.75;

  switch (openDirection) {
    case 'up':
      return {
        open: (position: number) => ({ y: -position, opacity }),
        closed: { y: 0 },
      };
    case 'down':
      return {
        open: (position: number) => ({ y: position, opacity }),
        closed: { y: 0 },
      };
    case 'right':
      return {
        open: (position: number) => ({ x: position, opacity }),
        closed: { x: 0 },
      };
    case 'left':
      return {
        open: (position: number) => ({ x: -position, opacity }),
        closed: { x: 0 },
      };
    default:
      return {
        open: (position: number) => ({ y: -position, opacity }),
        closed: { y: 0 },
      };
  }
};

const opacityVariants = {
  open: { opacity: 1 },
  closed: { opacity: 0 },
};

const AnimatedLi = ({ children, position, openDirection }: LiProps) => (
  <motion.li
    className="share-list__item"
    variants={itemVariants(openDirection)}
    custom={position}
    initial="closed"
    animate="open"
    exit="closed"
    transition={{
      damping: 100,
      duration: 0.3,
    }}
    whileHover={{ opacity: 1, scale: 1.1 }}
  >
    {children}
  </motion.li>
);

const renderShareButton = ({
  network,
  content,
  fill,
  handler,
  linkCopyText,
  showLinkCopyText,
  beforeOnClickProps,
}: ButtonProps) => {
  switch (network) {
    case 'facebook':
      return (
        <FacebookShareButton
          className="share-button__facebook"
          url={content.url}
          quote={content.facebookQuote}
          beforeOnClick={beforeOnClickProps?.facebook}
        >
          <Icon className="share-icon" name="facebook-circle" fill={fill} data-type={'facebook'} />
        </FacebookShareButton>
      );
    case 'twitter':
      return (
        <TwitterShareButton
          className="share-button__twitter"
          title={content.twitterTitle}
          url={content.url}
          via={content.twitterHandle}
          beforeOnClick={beforeOnClickProps?.twitter}
        >
          <Icon className="share-icon" name="x-circle" fill={fill} data-type={'twitter'} />
        </TwitterShareButton>
      );
    case 'email':
      return (
        <EmailShareButton
          className="share-button__email"
          url={content.url}
          subject={content.headline}
          body={content.headline}
          beforeOnClick={beforeOnClickProps?.email}
        >
          <Icon className="share-icon" name="email-circle" fill={fill} data-type={'email'} />
        </EmailShareButton>
      );
    case 'link':
      return (
        <div>
          {network === 'link' && (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            <AnimatePresence>
              {showLinkCopyText && (
                <motion.div
                  className="share-button__link--message"
                  variants={opacityVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                >
                  <Icon
                    className="share-button__link--message-icon"
                    name="checkmark"
                    fill={COLORS.mlbWhite}
                    data-type={'link'}
                  />
                  <span>{linkCopyText}</span>
                </motion.div>
              )}
            </AnimatePresence>
          )}
          <button className="share-icon__button share-button__link" onClick={handler}>
            <Icon className="share-icon" name="link-circle" fill={fill} data-type={'link'} />
          </button>
        </div>
      );
    default:
      return;
  }
};

const Share = ({
  className,
  content,
  fill = COLORS.mlbWhite,
  isLoading = false,
  label,
  linkCopyText = 'Link Copied!',
  nativeShare,
  networks = ['email', 'link'],
  openDirection = 'up',
  persistShareIcon = false,
  ariaLabel,
  closeButtonAriaLabel,
  beforeOnClick,
}: ShareProps) => {
  const [showLinks, setShowLinks] = useState(false);
  const [showLinkCopyText, setShowLinkCopyText] = useState(false);

  const handleShareClick = () => {
    const nav: { share: (options?: ShareData) => Promise<void> } = navigator; // https://stackoverflow.com/questions/47831741/property-share-does-not-exist-on-type-navigator?noredirect=1&lq=1
    if (nav.share) {
      nav
        .share({
          title: content.headline,
          url: content.url,
        })
        .catch((err: Error) => {
          if (err.message === 'Abort due to cancellation of share.') {
            return;
          } // handles for users hitting the cancel button
          setShowLinks(false);
        });
    } else if (typeof nativeShare === 'function') {
      nativeShare(content.url);
    } else {
      setShowLinks((prevState) => !prevState);
    }
  };

  const handleLinkShareClick = () => {
    copyToClipboard(content.url);
    setShowLinkCopyText(true);
    setTimeout(() => setShowLinkCopyText(false), 1000);
  };

  const ShareContent = isLoading ? (
    <Skeleton width="15px" height="15px" isCircle={true} />
  ) : (
    <button
      className="share-button share-button__square"
      onClick={handleShareClick}
      aria-label={ariaLabel || label}
      data-type={'share'}
    >
      <Icon className="share-icon__square" name="share-square" fill={fill} />
    </button>
  );

  const closeButton = persistShareIcon ? (
    ShareContent
  ) : (
    <motion.button
      initial={{ opacity: 0, rotate: 0 }}
      animate={{ opacity: 1, rotate: -180 }}
      exit={{ opacity: 0, rotate: 0 }}
      transition={{ damping: 100 }}
      className="share-button share-button__close"
      onClick={() => setShowLinks((prevState) => !prevState)}
      aria-label={closeButtonAriaLabel}
    >
      <Icon className="share-icon__close" name={persistShareIcon ? 'share-square' : 'close'} fill={fill} />
    </motion.button>
  );

  return (
    <Wrapper className={className} fill={fill} openDirection={openDirection}>
      {label && (
        <Label
          animate={{
            opacity: showLinks && openDirection === 'left' ? 0 : 1,
          }}
          color={fill}
          onClick={handleShareClick}
          aria-label={ariaLabel || label}
        >
          {label}
        </Label>
      )}
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-expect-error */}
      <AnimatePresence>
        {!showLinks && (
          <motion.div variants={opacityVariants} initial="closed" animate="open" exit="closed">
            {ShareContent}
          </motion.div>
        )}
      </AnimatePresence>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-expect-error */}
      <AnimatePresence>
        {showLinks && (
          <>
            {closeButton}
            <motion.ul
              variants={opacityVariants}
              initial="closed"
              animate="open"
              exit="closed"
              transition={{ ease: [0, 0.1, 0.83, 0.67] }}
              className="share-list"
            >
              {networks.map((network, idx) => (
                <AnimatedLi key={network} network={network} position={35 * (idx + 1)} openDirection={openDirection}>
                  {renderShareButton({
                    network,
                    content,
                    fill,
                    linkCopyText,
                    showLinkCopyText,
                    beforeOnClickProps: beforeOnClick,
                    handler: network === 'link' ? handleLinkShareClick : () => {},
                  })}
                </AnimatedLi>
              ))}
            </motion.ul>
          </>
        )}
      </AnimatePresence>
    </Wrapper>
  );
};

export default Share;
